import React from 'react';
import styled from 'styled-components';
import { Poppins } from 'src/common/Poppins';
import { Spacer } from 'src/common/Spacer';
import { WebGlobeSvg, ArrowUpRightSvg } from 'src/assets/dynamicSvg';
import { FrequencySource } from '../types';
import { LabelChip } from './LabelChip';
import { AcceptedPill } from './AcceptedPill';
import { SecondaryButton } from './SuggestionButtons';

const Card = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.white};
  padding: 20px 16px 24px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
`;

const LeftCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1 1 0;
  min-width: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: ${({ theme }) => theme.colors.stroke};
`;

const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
`;

const VisitLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 4px 12px;
  border-radius: 1000px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.cultured};
    border-color: ${({ theme }) => theme.colors.cflowerBlue};
  }
  &:hover .visit-text {
    text-decoration: underline;
  }
`;

interface FrequencySourceCardProps {
  source: FrequencySource;
  accepted: boolean;
  loading?: boolean;
  disabled?: boolean;
  onUse: () => void;
}

export const FrequencySourceCard: React.FC<FrequencySourceCardProps> = ({
  source,
  accepted,
  loading,
  disabled,
  onUse,
}) => (
  <Card>
    <HeaderRow>
      <LeftCol>
        <TitleRow>
          <WebGlobeSvg size={14} />
          <Poppins px={14} weight={500} color="prussianBlue">
            {source.title}
            {source.year ? ` (Published ${source.year})` : ''}
          </Poppins>
        </TitleRow>
        <Poppins px={16} weight={600} color="prussianBlue">
          {source.display}
        </Poppins>
      </LeftCol>
      {accepted ? (
        <AcceptedPill bordered />
      ) : (
        <SecondaryButton label="Use suggestion" loading={loading} disabled={disabled} onClick={onUse} />
      )}
    </HeaderRow>

    <Spacer $px={24} />
    <Divider />
    <Spacer $px={16} />

    <Pills>
      {source.industry && <LabelChip filled label="Industry" value={source.industry} />}
      <LabelChip filled label="Confidence" value={`${source.confidence}%`} />
      {source.url && (
        <VisitLink href={source.url} target="_blank" rel="noopener noreferrer">
          <Poppins className="visit-text" px={14} weight={500} color="prussianBlue">
            Visit link
          </Poppins>
          <ArrowUpRightSvg size={16} />
        </VisitLink>
      )}
    </Pills>

    {source.quote && (
      <>
        <Spacer $px={16} />
        <Poppins px={14} color="cflowerBlue" css="line-height: 1.7;">
          {`“${source.quote}”`}
        </Poppins>
      </>
    )}
  </Card>
);
